import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import * as cognito from "@/services/cognito";
import { apiService, setAuthTokenProvider } from "@/services/api";
import { decodeJwtPayload } from "@/utils/jwt";
import type { CognitoTokens, IdTokenClaims } from "@/types/auth";
import { primaryRole } from "@/types/auth";
import type { Me, Role } from "@/types/api";

const STORAGE_KEY = "bookbase.auth.tokens";
const REFRESH_SKEW_MS = 60_000;

type AuthState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "new-password-required"; username: string; session: string }
  | { status: "signed-in"; claims: IdTokenClaims; role: Role; me: Me }
  | { status: "error"; message: string };

interface AuthContextValue {
  state: AuthState;
  signIn: (username: string, password: string) => Promise<"signed-in" | "new-password-required">;
  confirmNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => string | null;
  getAccessToken: () => string | null;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredTokens(): CognitoTokens | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CognitoTokens) : null;
  } catch {
    return null;
  }
}

function storeTokens(tokens: CognitoTokens | null) {
  try {
    if (tokens) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore (private browsing, quota) */
  }
}

/** ADMIN/LIBRARIAN -> console's /management/me; TEACHER/STUDENT -> portal's /portal/me. */
async function loadMe(role: Role): Promise<Me> {
  const preferManagement = role === "ADMIN" || role === "LIBRARIAN";
  try {
    return await (preferManagement ? apiService.management.me() : apiService.portal.me());
  } catch {
    // Fallback (docs/SPEC.md 2.4): try the other app's /me before giving up.
    return preferManagement ? apiService.portal.me() : apiService.management.me();
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const tokensRef = useRef<CognitoTokens | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doRefreshRef = useRef<() => Promise<void>>(async () => {});

  const getIdToken = useCallback(() => tokensRef.current?.idToken ?? null, []);
  const getAccessToken = useCallback(() => tokensRef.current?.accessToken ?? null, []);

  useEffect(() => {
    setAuthTokenProvider(getIdToken);
  }, [getIdToken]);

  const scheduleRefresh = useCallback((tokens: CognitoTokens) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = Math.max(tokens.expiresAt - Date.now() - REFRESH_SKEW_MS, 5_000);
    refreshTimer.current = setTimeout(() => {
      void doRefreshRef.current();
    }, delay);
  }, []);

  const applyTokens = useCallback(
    async (tokens: CognitoTokens, { reloadMe }: { reloadMe: boolean }) => {
      tokensRef.current = tokens;
      storeTokens(tokens);
      scheduleRefresh(tokens);
      const claims = decodeJwtPayload(tokens.idToken);
      const role = primaryRole(claims);
      if (!role) {
        setState({ status: "error", message: "Dieses Konto hat keine Rolle." });
        return;
      }
      if (reloadMe) {
        const me = await loadMe(role);
        setState({ status: "signed-in", claims, role, me });
      } else if (state.status === "signed-in") {
        setState({ ...state, claims });
      }
    },
    [scheduleRefresh, state],
  );

  const doRefresh = useCallback(async () => {
    const current = tokensRef.current;
    if (!current?.refreshToken) {
      setState({ status: "signed-out" });
      return;
    }
    try {
      const outcome = await cognito.refreshTokens(current.refreshToken);
      if (outcome.tokens) await applyTokens(outcome.tokens, { reloadMe: false });
    } catch {
      tokensRef.current = null;
      storeTokens(null);
      setState({ status: "signed-out" });
    }
  }, [applyTokens]);

  useEffect(() => {
    doRefreshRef.current = doRefresh;
  }, [doRefresh]);

  useEffect(() => {
    const stored = readStoredTokens();
    if (!stored || stored.expiresAt <= Date.now()) {
      setState({ status: "signed-out" });
      return;
    }
    void applyTokens(stored, { reloadMe: true }).catch(() => setState({ status: "signed-out" }));
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(
    async (username: string, password: string): Promise<"signed-in" | "new-password-required"> => {
      const outcome = await cognito.initiateAuth(username, password);
      if (outcome.challenge) {
        setState({ status: "new-password-required", username: outcome.challenge.username, session: outcome.challenge.session });
        return "new-password-required";
      }
      if (outcome.tokens) {
        await applyTokens(outcome.tokens, { reloadMe: true });
        return "signed-in";
      }
      throw new Error("Cognito returned neither tokens nor a challenge.");
    },
    [applyTokens],
  );

  const confirmNewPassword = useCallback(
    async (newPassword: string) => {
      if (state.status !== "new-password-required") throw new Error("No pending challenge.");
      const outcome = await cognito.respondToNewPasswordChallenge(state.username, newPassword, state.session);
      if (outcome.tokens) await applyTokens(outcome.tokens, { reloadMe: true });
    },
    [state, applyTokens],
  );

  const signOut = useCallback(async () => {
    const token = tokensRef.current?.accessToken;
    tokensRef.current = null;
    storeTokens(null);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    setState({ status: "signed-out" });
    if (token) {
      try {
        await cognito.globalSignOut(token);
      } catch {
        /* already signed out server-side, or offline: the local session is cleared either way */
      }
    }
  }, []);

  const refreshMe = useCallback(async () => {
    if (state.status !== "signed-in") return;
    const me = await loadMe(state.role);
    setState({ ...state, me });
  }, [state]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signIn, confirmNewPassword, signOut, getIdToken, getAccessToken, refreshMe }),
    [state, signIn, confirmNewPassword, signOut, getIdToken, getAccessToken, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
